import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * The cookie endpoint has to sit on the API's origin, not the portal's.
 *
 * In development `apiUrl` is the relative `/api/v1` and the dev server proxies
 * `/sanctum` across, so a relative path is right. In staging and production
 * `apiUrl` is absolute and the API lives on another host, where a relative
 * path would ask the portal's own origin for a cookie only the API can set.
 */
function csrfCookieUrl(): string {
  const path = '/sanctum/csrf-cookie';

  if (!/^https?:\/\//i.test(environment.apiUrl)) {
    return path;
  }

  return new URL(path, environment.apiUrl).toString();
}

/**
 * Fetches Sanctum's XSRF cookie.
 *
 * The API is stateful (`statefulApi()`), and the portal's HTTP interceptor
 * sends every request with credentials and an `X-XSRF-TOKEN` header, so any
 * mutating call is CSRF-checked -- including unauthenticated ones. Without the
 * cookie in place first, those requests come back 419.
 *
 * Failures are swallowed: the request that follows will surface the real
 * problem with a message that fits what the caller was trying to do, which is
 * more useful than an error about a cookie.
 */
@Injectable({ providedIn: 'root' })
export class CsrfService {
  private readonly http = inject(HttpClient);
  private readonly url = csrfCookieUrl();

  prime(): Observable<void> {
    return this.http.get(this.url, { responseType: 'text' }).pipe(
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }
}
